import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Aperture, Sparkles, User, Key } from 'lucide-react';
import { PhotoShowcase } from './PhotoShowcase';

export const LoginPage: React.FC = () => {
  const { loginWithCode, error, loading, isAuthenticated } = useAuth();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) return;

    const success = await loginWithCode(name.trim(), code.trim());
    if (success) {
      window.location.href = '/';
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col lg:flex-row overflow-x-hidden">
      {/* Left Column - Photo Showcase (Desktop only, hidden on mobile) */}
      <div className="relative hidden lg:flex w-1/2 h-screen items-center justify-center p-12">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px'
            }}
          />
          {/* Gradient orbs */}
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-zinc-800/40 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-red-900/10 rounded-full blur-3xl" />
        </div>

        {/* Photo showcase component */}
        <PhotoShowcase />
      </div>

      {/* Divider line - Desktop only */}
      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-zinc-700 to-transparent" />

      {/* Right Column - Login (Desktop) / First Section (Mobile) */}
      <div className="relative w-full lg:w-1/2 min-h-screen lg:h-screen flex items-center justify-center p-6 lg:p-12">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-zinc-800/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-sm w-full">
          {/* Logo/Header with animated entrance */}
          <div className="text-center mb-8 animate-fade-in-up">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-zinc-900 border border-zinc-800 rounded-3xl mb-5 shadow-lg shadow-black/20 group hover:border-zinc-700 transition-all duration-300">
              <Aperture className="w-10 h-10 text-white group-hover:rotate-45 transition-transform duration-500" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight mb-2">Photomaton</h1>
            <p className="text-base text-zinc-400 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-red-500" />
              AI Photo Booth
              <Sparkles className="w-4 h-4 text-red-500" />
            </p>
          </div>

          {/* Feature highlights */}
          <div className="flex justify-center gap-4 mb-8 animate-fade-in-up animation-delay-100">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Instant Transform
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Multiple Styles
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <div className="w-1.5 h-1.5 rounded-full bg-pink-500" />
              High Quality
            </div>
          </div>

          {/* Login Card with hover effect */}
          <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8 shadow-xl shadow-black/20 hover:border-zinc-700 transition-all duration-300 animate-fade-in-up animation-delay-200">
            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 animate-shake">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Welcome text */}
            <p className="text-zinc-400 text-center mb-6 text-sm">
              Enter your name and access code to continue
            </p>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Input */}
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  disabled={loading}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl py-4 px-12 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all disabled:opacity-50"
                  autoComplete="username"
                  autoFocus
                />
              </div>

              {/* Code Input */}
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="password"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Access code"
                  disabled={loading}
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl py-4 px-12 text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all disabled:opacity-50"
                  autoComplete="current-password"
                />
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading || !name.trim() || !code.trim()}
                className="w-full bg-white hover:bg-zinc-100 hover:scale-[1.02] active:scale-[0.98] disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed disabled:scale-100 text-zinc-900 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 group shadow-lg shadow-white/10"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>

      {/* Mobile Photo Showcase Section - Below login */}
      <div className="relative lg:hidden w-full py-12 px-6">
        {/* Divider line */}
        <div className="h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent mb-12" />

        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-zinc-800/40 rounded-full blur-3xl animate-pulse-slow" />
        </div>

        {/* Photo showcase component */}
        <PhotoShowcase />
      </div>
    </div>
  );
};
